import classNames from 'classnames';
import React, { useState, useEffect } from 'react';
import { Check } from 'react-feather';
import { Button } from 'ui';
import { Text, TEXT_VARIANTS } from 'ui/Typography';

import QuestionPrompt from 'components/Practice/Learner/QuestionPrompt';
import SkippedOption from 'components/Practice/Recording/SkippedOption';
import QuestionElementPrompt from 'components/Questions/shared/QuestionElementPrompt';
import InfoTooltip from 'components/shared/InfoTooltip';
import PracticeRecordingQuestion from 'components/Questions/PracticeRecordingQuestion/PracticeRecordingQuestion';
import HighFiveShareableLink from 'components/shared/HighFiveShareableLink';
import recordingPermissionErrorTypes from 'constants/recordingPermissionErrorTypes.js';
import MediaPlayer from 'components/ui/MediaPlayer';
import request from 'utils/request_ajax';
import { audioFileFormats } from 'constants/mediaFileFormats';
import trackEvent from 'misc/trackEvent';
import { routes } from 'constants/config';
import ToggleButton from 'components/shared/ToggleButton';
import { useFeatureFlags } from 'components/shared/hooks';
import UniversalPlayer from 'components/shared/UniversalPlayer';
import SkippedMessage from './SkippedMessage';
import AudioOnMobileMessage from '../shared/AudioOnMobileMessage';
import UniversalUploaderComponent from '../../shared/UniversalUploader/UniversalVideoUploader';

import ErrorModal from '../shared/ErrorModal';
import RetryButton from '../shared/RetryButton';

const Question = ({
  data,
  meta,
  recordingConfig,
  hasZiggeoForAllMedia,
  i18n,
  seismicAudioPracticeUploadEnabled,
  saveMedia,
}) => {
  const [loading, setLoading] = useState(true);
  const [errorType, setErrorType] = useState(null);
  const [skipped, setSkipped] = useState(meta.previewing ? null : meta.skipped);
  const [disabled, setDisabled] = useState(meta.finished || meta.previewing);
  const [finished, setFinished] = useState(meta.finished);
  const [mediaToken, setMediaToken] = useState(
    !meta.skipped ? data.givenAnswer : null,
  );
  const [importTimingStarted, setImportTimingStarted] = useState(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [answerRecording, setAnswerRecording] = useState(
    data?.recording || null,
  );
  const [enabledAutomatedAnalysis, setEnabledAutomatedAnalysis] = useState(
    data?.enableAutomatedAnalysis || false,
  );
  const [technicalDifficultiesReported, setTechnicalDifficultiesReported] =
    useState(false);
  const [sendFeedbackError, setSendFeedbackError] = useState(false);
  const [tryAgainDisabled, setTryAgainDisabled] = useState(false);

  useEffect(() => {
    window.hippoWidget.onLoad(() => {
      setLoading(false);
    });
  }, [loading]);

  const showMediaButtons = !mediaToken && !skipped && !answerRecording;
  const showSkippedOption =
    !finished &&
    !skipped &&
    !mediaToken &&
    !meta.features.removeAbilityToSkipRecordingQuestions;

  const showReportTechnicalDifficulties =
    meta.features.removeAbilityToSkipRecordingQuestions;
  const isScreenRecording = recordingConfig.screenRecord;
  const showAudioOnMobileNote = meta.viewingOnMobile && !isScreenRecording;
  const mediaButtonClass = classNames({
    'QuestionElement--disabled': disabled,
  });
  const disabledMediaButtonClass = classNames({
    'ml-20': !meta.recordingSupported,
  });

  const recordButtonText = () => {
    if (isScreenRecording && hasZiggeoForAllMedia) {
      return t('lessons.editor.media_recording.record');
    }
    if (isScreenRecording) {
      return t('questions.question.hippo_screen_recording.record');
    }
    return t('questions.question.audio_recording.record');
  };

  const uploadButtonText = () => {
    if (isScreenRecording && hasZiggeoForAllMedia) {
      return t('questions.question.webcam_recording.upload');
    }
    if (isScreenRecording) {
      return t('questions.question.hippo_screen_recording.upload');
    }
    return t('questions.question.audio_recording.upload');
  };

  const setProvider = () => {
    if (seismicAudioPracticeUploadEnabled) {
      return 'seismic';
    }
    if (isScreenRecording && hasZiggeoForAllMedia) {
      return 'ziggeo';
    }
    return 'hippo_video';
  };

  const saveAnswer = (
    answer,
    provider = null,
    callback = null,
    format = null,
    seismicAudioData = null,
  ) => {
    const recordingProvider = answer && provider ? provider : setProvider();
    const isSeismicAudio = !!seismicAudioData?.containerId;
    request
      .postJSON(meta.saveUrl, {
        answer,
        question_id: data.questionId,
        provider: recordingProvider,
        file_format: format,
        file_name: seismicAudioData?.name,
        size: seismicAudioData?.size,
        containerId: seismicAudioData?.containerId,
        isSeismicAudio,
      })
      .then(resData => {
        if (callback) {
          callback();
        }
        const resRecording = resData.answer ? resData.answer.recording : null;
        setAnswerRecording(resRecording);
      })
      .catch(exception => {
        if (exception.responseJSON.error === 'lesson_completed') {
          setFinished(true);
          setDisabled(true);
        }
      });
  };

  const reloadRecording = () => {
    setTryAgainDisabled(true);
    saveAnswer(null, null, () => {
      setSkipped(false);
      setMediaToken(null);
      setVideoUploading(false);
      setRecording(false);
      setSendFeedbackError(false);
      setTechnicalDifficultiesReported(false);

      trackEvent('practice/recording_try_again_clicked', {
        content_type: 'question',
        content_id: data.questionId,
      });
    });
  };

  const trackingEventParameters = () => {
    const { progressId, questionId, questionType } = data;
    return {
      lesson_section_element_id: null,
      question_id: questionId,
      progress_id: progressId,
      recording_type: questionType.slice(0, questionType.indexOf('_')),
    };
  };

  const trackPermissionDenied = responseError => {
    const eventData = {
      ...trackingEventParameters(),
      upload: false,
      error_type: responseError,
    };
    trackEvent('hippo_video/permission_denied', eventData);
  };

  const trackRecordingError = responseError => {
    const eventData = {
      ...trackingEventParameters(),
      upload: false,
      error_type: responseError,
    };
    trackEvent('hippo_video/recording_error', eventData);
  };

  const trackRecordingInitiated = trackData => {
    const eventData = {
      ...trackingEventParameters(),
      token: trackData.token,
    };
    trackEvent('hippo_video/recording_initiated', eventData);
  };

  const trackVideoCancelled = isUpload => {
    const eventData = {
      ...trackingEventParameters(),
      upload: isUpload,
    };
    trackEvent('hippo_video/recording_cancelled', eventData);
  };

  const trackRecordingData = trackData => {
    const eventData = { asset_id: trackData.asset_id, token: trackData.token };
    trackEvent('recording_metadata', eventData);
  };

  const trackRecordingEvents = trackData => {
    trackRecordingInitiated(trackData);
    trackRecordingData(trackData);
  };

  const trackRecordingStarted = () => {
    const eventData = {
      ...trackingEventParameters(),
    };
    trackEvent('hippo_video/recording_started', eventData);
  };

  const trackRecordingAborted = () => {
    const eventData = {
      ...trackingEventParameters(),
    };
    trackEvent('hippo_video/recording_aborted', eventData);
  };

  const trackRecordingStopped = () => {
    const eventData = {
      ...trackingEventParameters(),
    };
    trackEvent('hippo_video/recording_stopped', eventData);
  };

  const trackImportStarted = () => {
    setImportTimingStarted(performance.now());
    trackEvent('practice/upload_started', {
      question_id: data.questionId,
    });
  };

  const trackSourceUploaded = isUpload => {
    const eventData = {
      ...trackingEventParameters(),
      upload: isUpload,
    };
    trackEvent('hippo_video/source_uploaded', eventData);
  };

  const trackImportComplete = trackingData => {
    const importTimingComplete = performance.now();
    const importTimingFinal = importTimingComplete - importTimingStarted;
    trackEvent('practice/upload_complete', {
      question_id: data.questionId,
      token: trackingData.token,
      duration: trackingData.media_duration,
      total_import_time: importTimingFinal,
    });
  };

  const trackImportFailure = error => {
    trackEvent('practice/upload_failed', {
      question_id: data.questionId,
      error,
    });
  };

  const trackVideoSubmitted = isUpload => {
    const eventData = {
      ...trackingEventParameters(),
      upload: isUpload,
    };
    trackEvent('hippo_video/video_submitted', eventData);
  };

  const showErrorModal = response => {
    const responseError = response.error;

    reloadRecording();
    if (Object.values(recordingPermissionErrorTypes).includes(responseError)) {
      trackPermissionDenied(responseError);
    } else {
      trackRecordingError(responseError);
    }
    if (
      responseError !==
      recordingPermissionErrorTypes.HIPPO_SCREEN_PERMISSION_DENIED_ERROR
    ) {
      setErrorType(errorType);
    }
  };

  const recordMediaToken = event => {
    const regex = /\/video\/play\/(.+)$/g;
    const updatedMediaToken = regex.exec(event.recorded_url)[1];
    setMediaToken(updatedMediaToken);
    if (!videoUploading) {
      saveAnswer(updatedMediaToken);
    }
  };

  const recordStopped = event => {
    trackRecordingStopped(event);
    setVideoUploading(true);
  };

  const sourceUploaded = () => {
    if (mediaToken) {
      saveAnswer(mediaToken);
    }
    setVideoUploading(false);
  };

  const handleResetError = () => {
    setErrorType(null);
  };
  const handleUploadError = error => {
    trackImportFailure(error);
    if (error.reason === 'invalid_file_format') {
      setErrorType(error.reason + '_video');
    } else if (error.reason === 'size_limit_exceeded') {
      setErrorType(error.reason);
    } else if (error.reason === 'processing_error') {
      if (error.error.includes('Media length is too short')) {
        setErrorType('video_too_small');
      } else if (
        error.error.includes('Video Codec Unidentified') ||
        error.error.includes('Audio Codec Unidentified')
      ) {
        setErrorType('unidentified_codec');
      }
    }
  };

  const handleRecord = () => {
    setRecording(true);
    window.hippoWidget.on('record_error', showErrorModal);
    window.hippoWidget.startRecording(recordingConfig, data.currentUserId);
    window.hippoWidget.on('record_initiated', trackRecordingEvents);
    window.hippoWidget.on('record_started', trackRecordingStarted);
    window.hippoWidget.on('record_aborted', () => {
      reloadRecording();
      trackRecordingAborted();
    });
    window.hippoWidget.on('record_stopped', recordStopped);
    window.hippoWidget.on('source_uploaded', () => {
      sourceUploaded();
      trackSourceUploaded(false);
    });
    window.hippoWidget.on('video_submitted', videoData => {
      recordMediaToken(videoData);
      trackVideoSubmitted(false);
    });
    window.hippoWidget.on('video_cancelled', () => {
      reloadRecording();
      trackVideoCancelled(false);
    });
  };

  const handleUpload = () => {
    window.hippoWidget.on('import_failed', handleUploadError);
    window.hippoWidget.import(data.currentUserId);
    window.hippoWidget.on('import_progress', properties => {
      if (properties.operation === 'started') {
        trackImportStarted();
      }
    });
    window.hippoWidget.on('source_uploaded', () => {
      trackSourceUploaded(true);
    });
    window.hippoWidget.on('import_complete', importData => {
      trackRecordingData(importData);
      trackImportComplete(importData);
    });
    window.hippoWidget.on('video_submitted', videoData => {
      recordMediaToken(videoData);
      trackVideoSubmitted(true);
    });
    window.hippoWidget.on('video_cancelled', cancelData => {
      reloadRecording(cancelData);
      trackVideoCancelled(true);
    });
  };

  const updateMediaTokenAndSave = (token, provider, format) => {
    setMediaToken(token);
    if (!videoUploading) {
      saveAnswer(token, provider, null, format, null);
    }
  };

  const saveAudioData = audioData => {
    saveAnswer(audioData?.storageId, null, null, null, audioData);
  };

  const canSkipWithReasonAndFeedback = (feedback, reason) => {
    return (
      feedback &&
      reason &&
      meta.skippedUrl &&
      !meta.features.removeAbilityToSkipRecordingQuestions
    );
  };

  const canReportTechnicalDifficulties = (feedback, reason) => {
    return (
      feedback &&
      reason &&
      meta.skippedUrl &&
      meta.features.removeAbilityToSkipRecordingQuestions
    );
  };

  const trackMediaAnswerSkipped = (feedback, reason, answer = null) => {
    trackEvent('practice/media_answer_skipped', {
      answer_id: answer?.id,
      question_id: data.questionId,
      question_type: data.questionType,
      skip_reason: reason,
      skip_feedback: feedback,
    });
  };

  const sendTechnicalIssueMailer = (reason, feedback, skippedState) => {
    request
      .postJSON(
        routes.PRACTICE.TECHNICAL_ISSUES_MAILER.CREATE(data.questionId),
        {
          assignor_id: data.assignorId,
          skip_reason: reason,
          skip_feedback: feedback,
        },
      )
      .then(resData => {
        if (!resData.success) {
          setSendFeedbackError(true);
        }
        setSkipped(skippedState);
        setTechnicalDifficultiesReported(!skippedState);
      });
  };

  const skipRecording = (feedback, reason) => {
    if (canSkipWithReasonAndFeedback(feedback, reason)) {
      request
        .postJSON(meta.skippedUrl, {
          question_id: data.questionId,
          skipped: true,
          feedback,
          reason,
        })
        .then(resData => {
          const answer = resData.answer?.answer;
          trackMediaAnswerSkipped(feedback, reason, answer);
        });

      sendTechnicalIssueMailer(reason, feedback, true);
    } else if (canReportTechnicalDifficulties(feedback, reason)) {
      trackMediaAnswerSkipped(feedback, reason);
      sendTechnicalIssueMailer(reason, feedback, false);
    } else if (meta.skippedUrl) {
      request.postJSON(meta.skippedUrl, {
        question_id: data.questionId,
        skipped: true,
      });
      setSkipped(true);
    }
  };

  const seismicBiometricCompliance =
    useFeatureFlags('readiness-enable-biometric-compliance') && !data.isOdp;

  return (
    <div className="bdrs-base PracticeQuestion">
      <QuestionElementPrompt
        required={data.required}
        subject={data.subject}
        questionSubjectClass={meta.questionSubjectClass}
        labelErrorClass={meta.labelErrorClass}
        isGradable={data.isGradable}
        elementHeaderClass={meta.elementHeaderClass}
        isPractice={data.isPractice}
        hasAutomatedAnalysis={data.hasAutomatedAnalysis}
      />
      {meta.features.practice &&
        (data.criteria?.length > 0 ||
          meta.exampleRecordingHtml ||
          meta.exampleRecordingId) && (
          <QuestionPrompt
            criteria={data.criteria}
            features={meta.features}
            exampleRecordingHtml={meta.exampleRecordingHtml}
            exampleRecordingId={meta.exampleRecordingId}
            isVimeoProvider={meta.isVimeoProvider}
            isSeismicAudioProvider={meta.isSeismicAudioProvider}
            apiKey={meta.apiKey}
            url={meta.playbackUrl}
          />
        )}
      <div className="Response d-f fd-c">
        {showMediaButtons && !isScreenRecording && (
          <>
            {seismicBiometricCompliance && data.hasAutomatedAnalysis && (
              <ToggleButton
                questionId={data.questionId}
                progressId={data.progressId}
                enabledAutomatedAnalysis={enabledAutomatedAnalysis}
                setEnabledAutomatedAnalysis={setEnabledAutomatedAnalysis}
              />
            )}
            <div
              className={classNames('AudioRecorder', {
                isLegacyAudioEnabledForQuestion: true,
              })}
            >
              <div className="QuestionElement-recordingActions">
                <div>
                  <Button
                    tall
                    customColor
                    loading={loading && !meta.previewing}
                    disabled={disabled || !meta.recordingSupported || recording}
                    onClick={handleRecord}
                    className={`${mediaButtonClass} ${disabledMediaButtonClass}`}
                  >
                    {recordButtonText()}
                  </Button>
                  {!meta.recordingSupported && (
                    <InfoTooltip
                      tooltip={t(
                        'questions.question.media_recording.unsupported',
                      )}
                      className="ml-8 tooltip-fixedWidth"
                    />
                  )}
                </div>

                {seismicAudioPracticeUploadEnabled ? (
                  <UniversalUploaderComponent
                    accept={audioFileFormats}
                    onComplete={saveAudioData}
                    uploadBtnText={t(
                      'questions.question.audio_recording.upload',
                    )}
                  />
                ) : (
                  <button
                    type="button"
                    className={`${mediaButtonClass} u-linkBtn mt-8 fz-bigger`}
                    disabled={loading || disabled}
                    onClick={handleUpload}
                  >
                    {uploadButtonText()}
                  </button>
                )}
                {showAudioOnMobileNote && <AudioOnMobileMessage />}
              </div>
            </div>
          </>
        )}

        {showMediaButtons && isScreenRecording && (
          <PracticeRecordingQuestion
            questionId={data.questionId}
            progressId={data.progressId}
            resetAnswer={reloadRecording}
            setAnswer={updateMediaTokenAndSave}
            prompt={data.subject}
            currentUserId={data.currentUserId}
            completedAt={meta.completedAt}
            apiKey={meta.apiKey}
            trackEvents
            events={{
              upload_started: 'practice/upload_started',
              upload_complete: 'practice/upload_complete',
            }}
            hasZiggeoForAllMedia={hasZiggeoForAllMedia}
            provider={hasZiggeoForAllMedia ? 'ziggeo' : 'hippo_video'}
            isPreview={meta.previewing}
            seismicBiometricCompliance={seismicBiometricCompliance}
            enabledAutomatedAnalysis={enabledAutomatedAnalysis}
            setEnabledAutomatedAnalysis={setEnabledAutomatedAnalysis}
            hasAutomatedAnalysis={data.hasAutomatedAnalysis}
          />
        )}

        {(showSkippedOption || showReportTechnicalDifficulties) &&
          !sendFeedbackError && (
            <SkippedOption
              skipRecording={skipRecording}
              disabled={disabled}
              showTechnicalDifficulties
              i18n={i18n}
            />
          )}

        <SkippedMessage
          mediaButtonClass={mediaButtonClass}
          disabledMediaButtonClass={disabledMediaButtonClass}
          uploadButtonText={uploadButtonText()}
          finished={finished}
          disabled={disabled}
          reloadRecording={reloadRecording}
          sendFeedbackError={sendFeedbackError}
          technicalDifficultiesReported={technicalDifficultiesReported}
          skipped={skipped}
          recordButtonText={recordButtonText()}
        />

        {answerRecording && (
          <div className="VideoEmbed-player">
            {seismicBiometricCompliance && data.hasAutomatedAnalysis && (
              <ToggleButton
                questionId={data.questionId}
                progressId={data.progressId}
                enabledAutomatedAnalysis={enabledAutomatedAnalysis}
                setEnabledAutomatedAnalysis={setEnabledAutomatedAnalysis}
                disabled={!!meta.completedAt}
              />
            )}
            {meta.isVimeoProvider ? (
              <UniversalPlayer
                id={answerRecording.id}
                videoType="Recording"
                isPractice
              />
            ) : (
              <MediaPlayer
                hasZiggeoForAllMedia={hasZiggeoForAllMedia}
                recording={answerRecording}
                ziggeoApiKey={meta.apiKey}
                customClass="Video Video--playable"
                audioType={
                  recordingConfig.audio && !recordingConfig.screenRecord
                    ? 'Recording'
                    : null
                }
              />
            )}
            <div className="Video-redo d-f fw-w jc-sb ai-c pos-r">
              <Text
                className="d-f ai-c"
                variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD_SUCCESS}
              >
                <Check className="mr-4" />
                {i18n.webcam_recording.recording_complete}
              </Text>
              {!finished && (
                <RetryButton
                  onClick={reloadRecording}
                  icon="new_video"
                  disabled={tryAgainDisabled}
                  setTryAgainDisabled={setTryAgainDisabled}
                />
              )}
              {finished && !meta.finished && (
                <Text className="d-f ai-c" variant={TEXT_VARIANTS.BODY}>
                  {t(
                    'questions.question.media_recording.response_already_submitted',
                  )}
                </Text>
              )}
            </div>
          </div>
        )}
      </div>
      {answerRecording && (
        <div className="w-fill pl-32 pr-32 pb-32">
          <HighFiveShareableLink
            questionId={data.questionId}
            progressId={data.progressId}
            hasAnswerBeenSubmitted={!!answerRecording}
            isGradable={data.isGradable}
          />
        </div>
      )}

      {errorType && (
        <ErrorModal errorType={errorType} onAction={handleResetError} />
      )}
    </div>
  );
};

export default Question;
